import React, { useState, useEffect, useRef } from "react";
import DragDrop from "../../DragDrop";
import { Clock, Pencil, Plus, SignOut, Trash, WarningCircle } from "@phosphor-icons/react";
import axiosInstance from "../../../api/axiosInstance";
import PageLoader from "../../loader/PageLoader";
import ImageLoader from "../../loader/ImageLoader";
import {toast} from "react-toastify";
// Cusines Data
import { cuisines } from "./data.js";
import { uploadImageToServer } from "../../../api/uploadImage";
import { GST_OPTIONS, DEFAULT_MENU_GST, describeWindows } from "../../../api/gst";


// Matches types.FoodType on the backend. The colours are the usual Indian
// menu convention: green veg, amber egg, red non-veg.
const FOOD_TYPES = [
    { value: "VEG", label: "Veg", color: "#1B8960" },
    { value: "EGG", label: "Egg", color: "#D98324" },
    { value: "NON_VEG", label: "Non-veg", color: "#C2311F" },
];

const DiningRoom = () => {
    const [formData, setFormData] = useState({
        name: "",
        type: "",
        price: "",
        availability_windows: [],
        gst_percentage: DEFAULT_MENU_GST,
        description: "",
        photo: "",
        food_type : "",
        is_bestseller : false,
        is_chef_recommended : false,
        is_available : true,
    });

    // console.log("Form Data : ",formData)
    const [showForm, setShowForm] = useState(false);
    const [menuItems, setMenuItems] = useState([]);
    const [editIndex, setEditIndex] = useState(null);
    const [uploadedImage, setUploadedImage] = useState(null);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const hotelId = localStorage.getItem("hotelId");
    const [errors, setErrors] = useState({});
    const imageRequestRef = useRef(0);

    useEffect(() => {
        fetchMenuItems();
    }, []);

    const fetchMenuItems = async () => {
        try {
            const response = await axiosInstance.get(
                `/api/v1/hotels/${hotelId}/restaurant/menu-items`,
            );
            setMenuItems(response.data.data || []);
            
            setIsPageLoading(false);
        } catch (error) {
            console.error("Failed to fetch menu items:", error);
            if(error.response.data.message==="Hotel's menu item info not found"){
                setMenuItems([]);
            }
            setIsPageLoading(false);
        }
    };

    // console.log("Menu Items : ",menuItems);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    };

    const handleCheckboxChange = (e) => {
        const { id, checked } = e.target;

        let field;
        if (id === "is_bestseller") field = "is_bestseller";
        else if (id === "is_available") field = "is_available";
        else if (id === "is_chef_recommended") field = "is_chef_recommended";

        if (field) {
            setFormData(prev => ({
                ...prev,
                [field]: checked,
            }));
        }
    };

    const handleAddWindow = () => {
        setFormData((prev) => ({
            ...prev,
            availability_windows: [
                ...prev.availability_windows,
                { start_time: "", end_time: "" },
            ],
        }));
        setErrors((prev) => ({ ...prev, availability_windows: "" }));
    };

    const handleRemoveWindow = (windowIndex) => {
        setFormData((prev) => ({
            ...prev,
            availability_windows: prev.availability_windows.filter(
                (_, index) => index !== windowIndex,
            ),
        }));
        setErrors((prev) => ({ ...prev, availability_windows: "" }));
    };

    const handleWindowChange = (windowIndex, field, value) => {
        setFormData((prev) => ({
            ...prev,
            availability_windows: prev.availability_windows.map((window, index) =>
                index === windowIndex ? { ...window, [field]: value } : window,
            ),
        }));
        setErrors((prev) => ({ ...prev, availability_windows: "" }));
    };

    const resetFormStates = () => {
        setFormData({
            name: "",
            type: "",
            price: "",
            availability_windows: [],
            gst_percentage: DEFAULT_MENU_GST,
            description: "",
            photo: "",
            food_type : "",
            is_bestseller : false,
            is_chef_recommended : false,
            is_available : true

        });
        setUploadedImage(null);
        setEditIndex(null);
        setErrors({});
    };

    const handleFileUpload = async (files) => {
        if (files.length > 0) {
            const file = files[0];
            // console.log("File to upload:", file);

            setUploadedImage({
                id: `temp_${Date.now()}`,
                isLoading: true,
                file: file,
            });

            try {
                // console.log("Starting upload for file:", file.name);
                const uploadUrlResponse = await axiosInstance.get(
                    `/api/v1/images/upload-presigned-url?file_name=${file.name}`,
                );
                const { key, url } = uploadUrlResponse.data;
                await uploadImageToServer(file, url);
                // console.log("Upload completed for file:", file.name);

                setUploadedImage({
                    id: `temp_${Date.now()}`,
                    key,
                    url: URL.createObjectURL(file),
                    isLoading: false,
                });

                setFormData((prevData) => ({ ...prevData, photo: key }));
                setErrors((prevErrors) => ({ ...prevErrors, photo: "" }));
            } catch (error) {
                console.error("Error uploading image:", error);
                setUploadedImage(null);
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    photo: "Failed to upload image. Please try again.",
                }));
            }
        }
    };

    const handleRemoveMediaFile = () => {
        setUploadedImage(null);
        setFormData((prevData) => ({ ...prevData, photo: "" }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = "Name is required";
        if (!formData.type) newErrors.type = "Cuisine is required";
        if (!formData.food_type) newErrors.food_type = "Veg / Egg / Non-veg is required";
        if (!formData.price) newErrors.price = "Price is required";
        const incomplete = formData.availability_windows.some(
            (window) => !window.start_time || !window.end_time,
        );
        const identical = formData.availability_windows.some(
            (window) => window.start_time && window.start_time === window.end_time,
        );
        if (incomplete) {
            newErrors.availability_windows =
                "Every time range needs both a start and an end";
        } else if (identical) {
            newErrors.availability_windows =
                "A time range cannot start and end at the same time";
        }

        // if (!formData.description.trim())
        //     newErrors.description = "Description is required";
        // if (!formData.photo) newErrors.photo = "At least one image is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle Submit
    const handleSaveMenuItem = async (e) => {
        e.preventDefault();
 
        if (!validateForm()) {
            return;
        }
        try {       
            setIsPageLoading(true);
            // console.log("Form Data before submit:", formData);
            // return;
            const finalFormData = {
                name: formData.name,
                type: formData.type,
                food_type : formData.food_type,
                description: formData.description,
                photo: formData.photo,
                price: parseInt(formData.price),
                is_bestseller: formData?.is_bestseller,
                availability_windows: formData.availability_windows,
                gst_percentage: parseInt(formData.gst_percentage, 10) || 0,
                is_chef_recommended: formData?.is_chef_recommended,
                is_available : formData?.is_available
            };


            // console.log("console data.", finalFormData);
            // return;
            if (editIndex !== null) {
                await axiosInstance.put(
                    `/api/v1/hotels/${hotelId}/restaurant/menu-items/${editIndex}`,
                    finalFormData,
                );
            } else {
                await axiosInstance.post(
                    `/api/v1/hotels/${hotelId}/restaurant/menu-items`,
                    finalFormData,
                );
            }

            await fetchMenuItems();
            resetFormStates();
            setShowForm(false);
        } catch (error) {
            console.error("Failed to save menu item:", error);
            toast.error(error?.response?.data?.message || "Some Error Occured");
        }
        finally{
            setIsPageLoading(false);
        }
    };


    const handleEditClick = (index) => {
        try{
            const selectedItem = menuItems.find((menuItem) => menuItem.id === index);

            if (!selectedItem) {
                console.error("Item not found for ID:", index);
                return;
            }
            // console.log("Edited Food Item :",selectedItem);
            const formEdit = {
                description : selectedItem?.description || "",
                id : selectedItem?.id || "",
                name : selectedItem?.name || "",
                photo : selectedItem?.photo || "",
                price : selectedItem?.price || '',
                type : selectedItem?.type || '',
                availability_windows : (selectedItem?.availability_windows || []).map(
                    (window) => ({
                        start_time : window.start_time,
                        end_time : window.end_time,
                    }),
                ),
                gst_percentage : selectedItem?.gst_percentage ?? DEFAULT_MENU_GST,
                food_type : selectedItem?.food_type || '',
                is_bestseller : selectedItem?.is_bestseller || false,
                is_chef_recommended : selectedItem?.is_chef_recommended || false,
                is_available : selectedItem?.is_available
            }
            // console.log("Form Edit : ",formEdit);
            // setFormData(selectedItem);
            setFormData(formEdit);
            setShowForm(true);
            setEditIndex(index);
            
        
            // Clear the previous item's image up front: without this, switching to an
            // item that has no photo leaves the old one on screen. The placeholder
            // drives the isLoading branch the renderer already handles.
            setUploadedImage(
                selectedItem.photo
                    ? { key: selectedItem.photo, url: null, isLoading: true }
                    : null,
            );
            if (selectedItem.photo) {
                fetchItemImage(selectedItem.photo);
            }

 
        }catch(error){
            console.log(error)
        }
    };

    const fetchItemImage = async (imageKey) => {
        // Two edits in quick succession leave two presigned-URL calls in flight with no
        // ordering guarantee, so a slower earlier response could overwrite a newer one.
        // Only the most recent request is allowed to set state.
        const requestId = ++imageRequestRef.current;
        try {
            const response = await axiosInstance.get(
                `/api/v1/images/download-presigned-url?key=${imageKey}`,
            );
            if (requestId !== imageRequestRef.current) return;
            const imageUrl = response.data.url;
            setUploadedImage({
                key: imageKey,
                url: imageUrl,
                isLoading: false,
            });
        } catch (error) {
            console.error("Error fetching image URL:", error);
            if (requestId !== imageRequestRef.current) return;
            setUploadedImage(null);
        }
    };

    const handleDeleteClick = async (index) => {
        try {
            // console.log("Deleted Food Item ID :",index);
            setIsPageLoading(true);
            const itemId = index;
            await axiosInstance.delete(
                `/api/v1/hotels/${hotelId}/restaurant/menu-items/${itemId}`,
            );
            await fetchMenuItems();
        } catch (error) {
            console.error("Error deleting menu item:", error);
        }
        finally{
            setIsPageLoading(false);
        }
    };

    const trimImgName = (name) => {
        return name && name.length > 12
            ? name.substring(0, 12) + "..."
            : name || "";
    };


    const groupedMenu = menuItems.reduce((acc, item) => {
        const cuisine = item.type || "Other";
        if (!acc[cuisine]) {
            acc[cuisine] = [];
        }
        acc[cuisine].push(item);
        return acc;
    }, {});

 

    return (
        <>
            {isPageLoading && <PageLoader />}
            <div className="flex w-full">

                <div className="border-r-[1.5px] px-4 w-[40%]">

                    {/* */}
                    <button
                        className={`h-[48px] ${
                            showForm
                                ? "bg-[#FFF2F2] text-[#FFBFC1] cursor-not-allowed"
                                : "bg-[#ff7378]"
                        } px-4 rounded-lg text-[16px] mb-3`}
                        onClick={() => {
                            setShowForm(true);
                            resetFormStates();
                        }}
                        disabled={showForm}
                    >
                        Add Food Item
                    </button>

                    {/* <div className="flex gap-2 flex-wrap">
                        {menuItems.length > 0
                            ? menuItems.map((item, index) => (
                                    <div
                                        className="border-[1.5px] rounded-md border-[#BFC4CC] bg-[#EEF0FB] flex gap-3 px-2 py-1 mb-2"
                                        key={index}
                                    >
                                      <p className="font-semibold">
                                          {item.name}
                                      </p>
                                      <div className="flex justify-end gap-1">
                                          <button
                                              className="rounded-md p-1 border-[1.5px] border-[#BFC4CC] h-[24px] w-[24px] bg-white"
                                              onClick={() =>
                                                  handleEditClick(index)
                                              }
                                          >
                                              <Pencil size={14} />
                                          </button>
                                          <button
                                              className="rounded-md p-1 border-[1.5px] border-[#BFC4CC] h-[24px] w-[24px] bg-white text-[#ff7378]"
                                              onClick={() =>
                                                  handleDeleteClick(index)
                                              }
                                          >
                                              <Trash size={14} />
                                          </button>
                                      </div>
                                    </div>
                              ))
                            : ""}
                    </div> */}
                    <div className="flex gap-2 flex-wrap">
                        {Object.entries(groupedMenu).map(([cuisine, items]) => (
                            <div key={cuisine}>
                                <h3 className="p-2 text-lg font-bold capitalize mb-2">
                                    {cuisine}
                                </h3>

                                <div className="flex gap-2 flex-wrap ml-4">
                                    {items.map((item, index) => (
                                    <div
                                        key={item.id || index}
                                        className="border-[1.5px] rounded-md border-[#BFC4CC] bg-[#EEF0FB] flex gap-3 px-2 py-1 mb-2 items-center"
                                    >
                                        <div>
                                            <p className="font-semibold">{item.name}</p>
                                            <p className="text-[#737880] text-xs">
                                                {describeWindows(item.availability_windows)}
                                                {item.gst_percentage
                                                    ? ` \u00b7 GST ${item.gst_percentage}%`
                                                    : ""}
                                            </p>
                                        </div>

                                        <div className="flex justify-end gap-1">
                                        <button
                                            className="rounded-md p-1 border-[1.5px] border-[#BFC4CC] h-[24px] w-[24px] bg-white"
                                            onClick={() => handleEditClick(item?.id)}
                                        >
                                            <Pencil size={14} />
                                        </button>
                                        <button
                                            className="rounded-md p-1 border-[1.5px] border-[#BFC4CC] h-[24px] w-[24px] bg-white text-[#ff7378]"
                                            onClick={() => handleDeleteClick(item?.id)}
                                        >
                                            <Trash size={14} />
                                        </button>
                                        </div>
                                    </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>


                </div>

                {showForm && (
                    <div className="w-[60%] px-4 self-start sticky top-0 max-h-[calc(100vh-80px)] overflow-y-auto">
                        <form onSubmit={(e)=>handleSaveMenuItem(e)}>

                            {/* Back */}
                            <div
                                onClick={() => {
                                    setShowForm(false);
                                    resetFormStates();
                                }}
                                className="flex justify-center items-center border-[1.5px] border-[#FF7378] px-4 py-2 rounded-lg font-semibold cursor-pointer gap-2 text-[#050f4e] mb-3">
                                Exit Food Item
                                <SignOut weight="fill" />
                            </div>

                            {/* submit */}
                            <button
                                className="h-[48px] bg-[#ff7378] w-full rounded-lg text-[16px]"
                                type="submit"
                            >
                                {editIndex !== null
                                    ? "Update Food Item"
                                    : "Add Food Item"}
                            </button>

                            <div className="flex flex-col gap-2 my-3">
                                {/* ... (other form fields remain the same) ... */}

                                {/* Name */}
                                <div>
                                    <p>Name</p>
                                    <input
                                        type="text"
                                        placeholder="Enter food item name"
                                        className="w-[70%] p-2 border-[1.5px] rounded-lg mt-1"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                    />
                                    {errors.name && (
                                        <span className="text-[#E47186] text-xs flex items-center gap-1">
                                            <WarningCircle className="text-sm" />
                                            {errors.name}
                                        </span>
                                    )}
                                </div>
                                
                                {/* Available Time */}
                                <div className="w-full">
                                    <p className="mb-2">Available Time</p>

                                    {formData.availability_windows.length === 0 ? (
                                        // The empty state is itself the add control: with no rows to
                                        // sit under, a separate button gives the eye two places to
                                        // look for one action.
                                        <button
                                            type="button"
                                            onClick={handleAddWindow}
                                            className="w-full text-left flex items-center gap-3 border-[1.5px] border-dashed border-[#BFC4CC] rounded-md px-3 py-3"
                                        >
                                            <Clock size={20} className="text-[#737880]" />
                                            <div className="flex-1">
                                                <p className="text-[#595E66] font-semibold text-sm">
                                                    Available all day
                                                </p>
                                                <p className="text-[#737880] text-xs">
                                                    Add a range to limit when guests can order this.
                                                </p>
                                            </div>
                                            <span className="flex items-center gap-1 text-[#0B22B2] text-sm font-semibold">
                                                <Plus size={14} weight="bold" />
                                                Add time range
                                            </span>
                                        </button>
                                    ) : (
                                        <>
                                            {formData.availability_windows.map((window, windowIndex) => {
                                                // Zero-padded 24h strings compare lexicographically, so an
                                                // end that sorts before the start is a window running past
                                                // midnight rather than an input mistake.
                                                const wrapsMidnight =
                                                    window.start_time &&
                                                    window.end_time &&
                                                    window.start_time > window.end_time;

                                                return (
                                                    <div className="mb-2" key={windowIndex}>
                                                        <div className="flex w-full gap-2 items-center">
                                                            <input
                                                                type="time"
                                                                aria-label="Available from"
                                                                className="border-[1.5px] rounded-md flex-1 py-2 px-2"
                                                                value={window.start_time || ""}
                                                                onChange={(e) =>
                                                                    handleWindowChange(
                                                                        windowIndex,
                                                                        "start_time",
                                                                        e.target.value,
                                                                    )
                                                                }
                                                            />
                                                            <span className="text-[#737880] text-sm">to</span>
                                                            <input
                                                                type="time"
                                                                aria-label="Available until"
                                                                className="border-[1.5px] rounded-md flex-1 py-2 px-2"
                                                                value={window.end_time || ""}
                                                                onChange={(e) =>
                                                                    handleWindowChange(
                                                                        windowIndex,
                                                                        "end_time",
                                                                        e.target.value,
                                                                    )
                                                                }
                                                            />
                                                            <button
                                                                type="button"
                                                                title="Remove this range"
                                                                className="p-1"
                                                                onClick={() => handleRemoveWindow(windowIndex)}
                                                            >
                                                                <Trash className="text-[#E47186]" />
                                                            </button>
                                                        </div>
                                                        {wrapsMidnight && (
                                                            <p className="text-[#737880] text-xs mt-1">
                                                                Runs past midnight into the next day.
                                                            </p>
                                                        )}
                                                    </div>
                                                );
                                            })}

                                            {/* Sits under the last row, where the eye already is after
                                                filling one in, rather than back up beside the heading. */}
                                            <button
                                                type="button"
                                                onClick={handleAddWindow}
                                                className="flex items-center gap-1 text-[#0B22B2] text-sm font-semibold border-[1.5px] border-dashed border-[#0B22B2] rounded-md px-2 py-1.5 w-full justify-center"
                                            >
                                                <Plus size={14} weight="bold" />
                                                Add another range
                                            </button>
                                        </>
                                    )}

                                    {errors.availability_windows && (
                                        <span className="text-[#E47186] text-xs flex items-center gap-1 mt-1">
                                            <WarningCircle className="text-sm" />
                                            {errors.availability_windows}
                                        </span>
                                    )}
                                </div>

                                {/* Type */}
                                <div className="flex w-full gap-3">
                                    <div className="w-full">
                                        <p>Type</p>
                                        <select
                                            name="type"
                                            value={formData.type}
                                            onChange={handleChange}
                                            className="border-[1.5px] rounded-md w-full py-2 px-1"
                                        >
                                            <option value="">
                                                Choose Type
                                            </option>
                                            {
                                                // A category this hotel already
                                                // uses but the list does not
                                                // carry, so editing keeps it
                                                // instead of blanking the field.
                                                formData.type &&
                                                !cuisines.includes(formData.type) && (
                                                    <option value={formData.type}>
                                                        {formData.type}
                                                    </option>
                                                )
                                            }
                                            {
                                                cuisines.map((cusine,index)=>{
                                                    return <option value={cusine} key={index}>
                                                        {cusine || "Cusine"}
                                                    </option>
                                                })
                                            }
                                        </select>
                                        {errors.type && (
                                            <span className="text-[#E47186] text-xs flex items-center gap-1">
                                                <WarningCircle className="text-sm" />
                                                {errors.type}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Veg / Egg / Non-veg */}
                                <div className="flex w-full gap-3">
                                    <div className="w-full">
                                        <p>Food Type</p>
                                        <div className="flex gap-4 mt-1">
                                            {FOOD_TYPES.map((option) => (
                                                <label
                                                    key={option.value}
                                                    className="flex items-center gap-1 cursor-pointer"
                                                >
                                                    <input
                                                        type="radio"
                                                        name="food_type"
                                                        value={option.value}
                                                        checked={
                                                            formData.food_type ===
                                                            option.value
                                                        }
                                                        onChange={handleChange}
                                                    />
                                                    <span
                                                        className="inline-flex items-center justify-center border-[1.5px] rounded-[3px]"
                                                        style={{
                                                            width: "14px",
                                                            height: "14px",
                                                            borderColor: option.color,
                                                        }}
                                                    >
                                                        <span
                                                            className="rounded-full"
                                                            style={{
                                                                width: "7px",
                                                                height: "7px",
                                                                background: option.color,
                                                            }}
                                                        />
                                                    </span>
                                                    <span className="text-sm">
                                                        {option.label}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                        {errors.food_type && (
                                            <span className="text-[#E47186] text-xs flex items-center gap-1">
                                                <WarningCircle className="text-sm" />
                                                {errors.food_type}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Checkbox */}
                                <div>
                                    {/* Best seller */}
                                    <div className="my-2 w-full flex gap-4">
                                        <label htmlFor="is_bestseller" className="flex items-center gap-6 cursor-pointer">
                                            <span>Is it Best Seller?</span>
                                            <input
                                                type="checkbox"
                                                id="is_bestseller"
                                                checked={formData.is_bestseller}
                                                onChange={(e)=>handleCheckboxChange(e)}
                                            />
                                        </label>
                                    </div>

                                    {/* Chef Recommended */}
                                    <div className="my-2 w-full flex gap-4">
                                        <label htmlFor="is_chef_recommended" className="flex items-center gap-6 cursor-pointer">
                                            <span>Is it chef recommended?</span>
                                            <input
                                                type="checkbox"
                                                id="is_chef_recommended"
                                                checked={formData.is_chef_recommended}
                                                onChange={(e)=>handleCheckboxChange(e)}
                                            />
                                        </label>
                                    </div>

                                    {/* Available Now */}
                                    <div className="my-2 w-full flex gap-4">
                                        <label htmlFor="is_available" className="flex items-center gap-6 cursor-pointer">
                                            <span>Is Available Now ?</span>
                                            <input
                                                type="checkbox"
                                                id="is_available"
                                                checked={formData?.is_available}
                                                onChange={(e)=>handleCheckboxChange(e)}
                                            />
                                        </label>
                                    </div>

                                </div>

                                {/* Price */}
                                <div>
                                    <p>Price</p>
                                    <div className="w-full flex gap-2 p-2 border-[1.5px] rounded-lg ">
                                        <p>₹</p>
                                        <input
                                            type="number"
                                            placeholder="Enter price"
                                            className="w-full outline-none border-none"
                                            name="price"
                                            value={formData.price}
                                            onChange={handleChange}
                                        />
                                    </div>{" "}
                                    {errors.price && (
                                        <span className="text-[#E47186] text-xs flex items-center gap-1">
                                            <WarningCircle className="text-sm" />
                                            {errors.price}
                                        </span>
                                    )}
                                </div>

                                {/* GST */}
                                <div className="w-full">
                                    <p>GST</p>
                                    <select
                                        name="gst_percentage"
                                        value={formData.gst_percentage}
                                        onChange={handleChange}
                                        className="border-[1.5px] rounded-md w-full py-2 px-1"
                                    >
                                        {GST_OPTIONS.map((option) => (
                                            <option key={option} value={option}>
                                                {option}%
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Description */}
                                <div>
                                    <p>Description</p>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        className="border-[1.5px] rounded-lg p-2 w-full"
                                        placeholder="Enter additional info"
                                        rows={5}
                                    ></textarea>
                                    {errors.description && (
                                        <span className="text-[#E47186] text-xs flex items-center gap-1">
                                            <WarningCircle className="text-sm" />
                                            {errors.description}
                                        </span>
                                    )}
                                </div>

                                {/* Image Upload */}
                                <div>
                                    <p>Upload Media</p>
                                    <div>
                                        <DragDrop
                                            onFilesUploaded={handleFileUpload}
                                            uploadedImages={
                                                uploadedImage
                                                    ? [uploadedImage]
                                                    : []
                                            }
                                            onRemoveMediaFile={
                                                handleRemoveMediaFile
                                            }
                                            acceptedFileTypes={{
                                                "image/*": [
                                                    ".jpeg",
                                                    ".png",
                                                    ".jpg",
                                                ],
                                            }}
                                            maxFiles={1}
                                            dragDropText="Drag and drop a food item image here"
                                        />
                                    </div>
                                    {errors.photo && (
                                        <span className="text-[#E47186] text-xs flex items-center gap-1">
                                            <WarningCircle className="text-sm" />
                                            {errors.photo}
                                        </span>
                                    )}
                                    <div>
                                        {uploadedImage && (
                                            <div className="flex gap-2 w-full mt-2 h-full flex-nowrap overflow-x-auto">
                                                <div className="w-[158px] h-[142px] rounded-lg border-[1.5px] border-[#BFC4CC] flex-shrink-0 overflow-hidden">
                                                    {uploadedImage.isLoading ? (
                                                        <ImageLoader />
                                                    ) : (
                                                        <>
                                                            <img
                                                                src={
                                                                    uploadedImage.url
                                                                }
                                                                alt="Food Item"
                                                                className="w-full h-[110px] bg-cover"
                                                            />
                                                            <div className="flex justify-between items-center h-[32px] px-1 bg-[#EEF0FB]">
                                                                <p>
                                                                    {trimImgName(
                                                                        uploadedImage.key,
                                                                    )}
                                                                </p>
                                                                <button
                                                                    className="rounded-md p-1 border-[1.5px] border-[#F8D9DE] text-[#DC4662] h-[24px] w-[24px] bg-white"
                                                                    onClick={
                                                                        handleRemoveMediaFile
                                                                    }
                                                                    type="button"
                                                                >
                                                                    <Trash
                                                                        size={
                                                                            14
                                                                        }
                                                                    />
                                                                </button>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>


                            </div>

                        </form>
                    </div>
                )}

            </div>
        </>
    );
};

export default DiningRoom;
